import React from 'react';
import { Link } from 'react-router-dom';
import { useAppSelector } from '../../store';
import Icon from '../../components/Icon';
import type { IconType } from 'react-icons';
import {
    FiVideo,
    FiRadio,
    FiPackage,
    FiFileText,
    FiRefreshCw,
} from 'react-icons/fi';

type NavTile = {
    to: string;
    label: string;
    description: string;
    icon: IconType;
};

function TileLink({ to, label, description, icon: TileIcon }: NavTile) {
    return (
        <Link
            to={to}
            className="flex items-start gap-3 rounded-lg border border-gray-200 p-4 text-left transition hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-700/80"
        >
            <span className="mt-0.5 shrink-0 text-blue-600 dark:text-blue-400">
                <Icon icon={TileIcon} className="h-5 w-5" />
            </span>
            <span className="min-w-0">
                <span className="block font-medium text-gray-900 dark:text-gray-100">{label}</span>
                <span className="mt-0.5 block text-sm text-gray-500 dark:text-gray-400">{description}</span>
            </span>
        </Link>
    );
}

function SectionCard({
    title,
    subtitle,
    children,
}: {
    title: string;
    subtitle?: string;
    children: React.ReactNode;
}) {
    return (
        <section className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-800">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
            {subtitle ? (
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{subtitle}</p>
            ) : null}
            <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">{children}</div>
        </section>
    );
}

/**
 * Dashboard for hardware / guarding incharge roles (not MIS — MIS uses MisInchargeDashboard).
 * Sections and links mirror {@link ../../layout/sidebars/InchargeSidebar InchargeSidebar}.
 */
export default function InchargeDashboard() {
    const pso = useAppSelector((s) => s.user.pso);
    const designation = pso?.designation || '';

    const inchargeType = designation.replace(/_/g, ' ').toLowerCase();
    const isCCTV = inchargeType.includes('cctv');
    const isBAMS = inchargeType.includes('bams');
    const isGuarding = inchargeType.includes('guarding');
    const showHardware = isCCTV || isBAMS;
    const showInventory = !isGuarding && (isCCTV || isBAMS);

    const hardwareTiles: NavTile[] = [];
    if (isCCTV) {
        hardwareTiles.push({
            to: '/manage-hardware-cctv',
            label: 'CCTV Hardware',
            description: 'Manage CCTV hardware installations',
            icon: FiVideo,
        });
    }
    if (isBAMS) {
        hardwareTiles.push({
            to: '/manage-hardware-bams',
            label: 'BAMS Hardware',
            description: 'Manage BAMS hardware installations',
            icon: FiRadio,
        });
    }

    const requestTiles: NavTile[] = [
        {
            to: '/request-form',
            label: 'View Requests',
            description: 'Open the request form and track status',
            icon: FiFileText,
        },
    ];
    if (isCCTV) {
        requestTiles.push({
            to: '/cctv',
            label: 'CCTV Hardware Request',
            description: 'CCTV hardware request workflow',
            icon: FiVideo,
        });
    }
    if (isBAMS) {
        requestTiles.push({
            to: '/bams',
            label: 'BAMS Hardware Request',
            description: 'BAMS hardware request workflow',
            icon: FiRadio,
        });
    }
    requestTiles.push({
        to: '/change-of-vendor',
        label: 'Change of Vendor',
        description: 'Review change of vendor requests',
        icon: FiRefreshCw,
    });

    const inventoryTiles: NavTile[] = [];
    if (isCCTV) {
        inventoryTiles.push({
            to: '/inventory?category=CCTV',
            label: 'CCTV Items',
            description: 'Inventory filtered to CCTV category',
            icon: FiPackage,
        });
    }
    if (isBAMS) {
        inventoryTiles.push({
            to: '/inventory?category=BAMS',
            label: 'BAMS Items',
            description: 'Inventory filtered to BAMS category',
            icon: FiPackage,
        });
    }

    return (
        <div className="space-y-6 p-6">
            <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                    {designation || 'Incharge'} dashboard
                </h2>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                    Welcome back, {pso?.name || 'User'}. Use the shortcuts below — they match your sidebar menu.
                </p>
            </div>

            {showHardware ? (
                <SectionCard title="Hardware Management" subtitle="Manage hardware installations">
                    {hardwareTiles.map((t) => (
                        <TileLink key={t.to} {...t} />
                    ))}
                </SectionCard>
            ) : null}

            <SectionCard title="Requests" subtitle={isGuarding && !isCCTV && !isBAMS ? 'View request status' : 'View request status and workflows'}>
                {requestTiles.map((t) => (
                    <TileLink key={`${t.to}-${t.label}`} {...t} />
                ))}
            </SectionCard>

            {showInventory ? (
                <SectionCard title="Inventory" subtitle="Track and manage items">
                    {inventoryTiles.map((t) => (
                        <TileLink key={t.to} {...t} />
                    ))}
                </SectionCard>
            ) : null}

            {isGuarding && !isCCTV && !isBAMS ? (
                <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50/80 p-4 text-sm text-gray-600 dark:border-gray-600 dark:bg-gray-900/40 dark:text-gray-400">
                    Your role focuses on guarding-related requests and vendor changes. Hardware and inventory shortcuts
                    appear when your designation includes CCTV or BAMS.
                </div>
            ) : null}
        </div>
    );
}
